/**
 * A record beside the view (WS-22): opened from wherever it's named — a
 * draft paragraph's citation chips, an agent's words, the ratings, the
 * Assistant's links, an ask — read whole by its id and shown in the right
 * column, which stays in view as the page scrolls. The view it was opened
 * from keeps its place and its keys; closing the record brings back what the
 * column held before (the Assistant, if it was open).
 */

import { useMemo } from 'react';

import { Anchor, CloseButton, Group, Loader, Stack, Text } from '@mantine/core';

import { useWorkbenchChecklist, useWorkbenchEvidenceItem } from '@/hooks/useWorkbench';
import type { WorkbenchHeader } from '@/types/workbench';
import { RecordView } from './RecordView';
import classes from './Workbench.module.css';

interface RecordColumnProps {
  header: WorkbenchHeader;
  recordId: string;
  /** In the column beside the view (sticky), not a drawer. */
  docked: boolean;
  onClose: () => void;
  /** Show it in the Evidence view, among the case's other records. */
  onShowInEvidence: (recordId: string) => void;
  onOpenChecklistItem: (itemId: string) => void;
}

export function RecordColumn({
  header,
  recordId,
  docked,
  onClose,
  onShowInEvidence,
  onOpenChecklistItem,
}: Readonly<RecordColumnProps>) {
  const record = useWorkbenchEvidenceItem(header.reportId, recordId, header.version);
  const checklist = useWorkbenchChecklist(header.reportId, header.version);
  const titles = useMemo(
    () => new Map((checklist.data ?? []).map((c) => [c.id, c.title])),
    [checklist.data]
  );
  const subjectName = record.data?.subjectId
    ? (header.subjects.find((s) => s.id === record.data?.subjectId)?.name ?? null)
    : null;
  return (
    <Stack
      gap="sm"
      className={classes.recordColumn}
      data-docked={docked}
      data-testid="record-column"
    >
      <Group justify="space-between" wrap="nowrap">
        <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.04em' }}>
          The record
        </Text>
        <CloseButton size="sm" onClick={onClose} aria-label="Close the record" />
      </Group>
      {record.data ? (
        <>
          <RecordView
            record={record.data}
            reportId={header.reportId}
            subjectName={subjectName}
            checklistTitles={titles}
            onOpenChecklistItem={onOpenChecklistItem}
          />
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => onShowInEvidence(recordId)}
            style={{ alignSelf: 'flex-start' }}
            data-testid="record-show-in-evidence"
          >
            See it among the case&apos;s records
          </Anchor>
        </>
      ) : record.error ? (
        <Text size="sm" c="dimmed">
          This record isn&apos;t on this case.
        </Text>
      ) : (
        <Loader size="sm" />
      )}
    </Stack>
  );
}
